import styled from 'styled-components';

import { PopupTheme } from './index';
import { StyleColor, NewStyleColor, BaseStyledComponentProps } from '../../utils/styles-constants';
import { hexToRgba, overlayAnimation, isSupportingBackdropBlur, spin } from '../../utils/style';

interface PopupProps {
	theme: {
		theme: PopupTheme;
	};
}

interface OverlayProps {
	isOpen: boolean;
}

interface ButtonCancelProps extends BaseStyledComponentProps {
	color?: string;
}

interface ButtonConfirmProps extends BaseStyledComponentProps {
	disabled: boolean;
	color?: string;
}

const Overlay = styled('div')`
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 100;
	background: ${isSupportingBackdropBlur()
		? hexToRgba(StyleColor.White, 50)
		: hexToRgba(StyleColor.Black, 90)};
	${(props: OverlayProps) => overlayAnimation(props.isOpen)};
`;

const OverlayBlur = styled('div')`
	${isSupportingBackdropBlur() ? 'backdrop-filter: blur(20px);' : ''}
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
`;

const PopupContainer = styled('div')`
	box-sizing: border-box;
	width: calc(100% - 20px);
	max-width: 480px;
	border-radius: 13px;
	background-color: ${StyleColor.White};
	box-shadow: 0 3px 35px 0 ${hexToRgba(StyleColor.Black, isSupportingBackdropBlur() ? 20 : 50)};

	${({ theme: { theme } }: PopupProps) => theme === PopupTheme.DARK && `
		background-color: ${NewStyleColor.Grey900};
	`}
`;

const PopupHeader = styled('div')`
	box-sizing: border-box;
	position: relative;
	padding: 20px;
	border-bottom: 2px solid ${StyleColor.Grey100};

	${({ theme: { theme } }: PopupProps) => theme === PopupTheme.DARK && `
		border-color: ${hexToRgba(StyleColor.White, 10)};
	`}
`;

const PopupContentWrapper = styled('div')`
	position: relative;
`;

const getButtonCommonStyles = (color: string) => `
	padding: 0;
	border: 0;
	outline: 0;
	background-color: transparent;
	font-family: HelveticaNeue, sans-serif;
	font-weight: 500;
	font-size: 16px;
	line-height: 20px;
	color: ${color};
	letter-spacing: 0.5px;
	cursor: pointer;
`;

const ButtonCancel = styled('button')`
	${(props: ButtonCancelProps) => getButtonCommonStyles(props.color || props.theme.primary.actionColor)}
	position: absolute;
	top: 19px;
	left: 15px;

	${({ theme: { theme } }: PopupProps) => theme === PopupTheme.DARK && `
		color: ${NewStyleColor.Blue450};
	`}
`;

const ButtonConfirm = styled('button')`
	${(props: ButtonConfirmProps) => getButtonCommonStyles(props.color || props.theme.primary.actionColor)}
	position: absolute;
	top: 19px;
	right: 15px;

	${(props: ButtonConfirmProps) => props.disabled && `
		cursor: default;
		opacity: 0.5;
	`}

	${({ theme: { theme } }: PopupProps) => theme === PopupTheme.DARK && `
		color: ${NewStyleColor.Blue450};
	`}
`;

const PopupBody = styled('div')`
	box-sizing: border-box;
	font-family: HelveticaNeue, sans-serif;
	font-size: 16px;
	line-height: 20px;
	color: ${StyleColor.Black};
`;

const Label = styled('span')`
	display: block;
	font-family: HelveticaNeue, sans-serif;
	font-weight: 500;
	font-size: 18px;
	line-height: 20px;
	color: ${StyleColor.Black};
	text-align: center;

	${({ theme: { theme } }: PopupProps) => theme === PopupTheme.DARK && `
		color: ${hexToRgba(StyleColor.White, 75)};
	`}
`;

const PopupFooter = styled('div')`
	padding: 20px;
	border-top: 2px solid ${StyleColor.Grey100};
	display: flex;
	align-items: center;
	justify-content: center;

	${({ theme: { theme } }: PopupProps) => theme === PopupTheme.DARK && `
		border-color: ${hexToRgba(StyleColor.White, 10)};
	`}
`;

const ButtonAccept = styled('button')`
	${(props: BaseStyledComponentProps) => getButtonCommonStyles(props.theme.primary.actionColor)}

	${({ theme: { theme } }: PopupProps) => theme === PopupTheme.DARK && `
		color: ${NewStyleColor.Blue450};
	`}
`;

const Loader = styled('div')`
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 10px;
	font-weight: bold;
	font-size: 21px;
	z-index: 10;
	background-color: ${NewStyleColor.White};
	color: ${NewStyleColor.Grey500};

	svg {
		height: 35px;
		width: 35px;
		animation: ${spin};
		margin-right: 20px;
	}
`;

export {
	Overlay,
	OverlayBlur,
	PopupContainer,
	PopupHeader,
	PopupContentWrapper,
	ButtonCancel,
	ButtonConfirm,
	PopupBody,
	Label,
	PopupFooter,
	ButtonAccept,
	Loader,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Popup/styles.ts